<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                        enter-from="translate-x-full" enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500 sm:duration-700"
                        leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl" @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white" v-if="form.id">
                                                Update Quote `{{ form.name }}`
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white" v-else>
                                                Create New Quote
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                    class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                    @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to create a new quote.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div>
                                                    <label for="quote-name" class="block text-sm text-gray-900 required">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="quote-name" v-model="form.name"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.name}" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="client-name"
                                                        class="block text-sm text-gray-900 required">
                                                        Client
                                                    </label>
                                                    <div class="mt-1 mb-3">
                                                        <v-select v-if="clientOverride === true || (clientOverride === false && !fixture.client.groups)" :disabled="clientOverride === false" class="h-full mt-1" :options="clients" label="name" :reduce="organisations => organisations.id" v-model="form.client_id" placeholder="Select Client" />
                                                        <select id="client-name" name="client-name" v-model="form.client_id"
                                                            v-if="clientOverride === false && fixture.client.groups && fixture.client.groups.length > 0"
                                                            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Select a client</option>
                                                            <optgroup v-for="group in fixture.client.groups" :label="group.name">
                                                                <option v-for="client in group.organisations" :value="client.id" v-html="client.name"></option>
                                                            </optgroup>
                                                        </select>
                                                    </div>
                                                    <span class="flex text-sm mt-3 underline cursor-pointer items-center" v-if="clientOverride === false" @click="overrideClient"><ArrowCircleRightIcon class="w-4 h-4 mr-2"/> Select a different client.</span>
                                                    <span class="flex text-sm mt-3 underline cursor-pointer items-center" v-if="clientOverride === true" @click="revertToFixtureClient"><RefreshIcon class="w-4 h-4 mr-2"/>Revert to fixture client.</span>
                                                </div>
                                                <div>
                                                    <label class="block text-sm text-gray-900">
                                                        Status
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="client-name" v-model="form.status"
                                                            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Select a status</option>
                                                            <option v-for="(status, index) in quote_statuses" :value="status" v-html="status"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div v-if="form.status === 'Declined'">
                                                    <label class="block text-sm text-gray-900 mb-1">
                                                        Reason for being declined:
                                                    </label>
                                                    <quill-editor content-type="html" theme="snow"
                                                        v-model:content="form.decline_reason"
                                                        class="min-h-[150px] mt-2 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                        :class="{'border-red-500': form.errors.decline_reason}">
                                                    </quill-editor>
                                                </div>
                                                <div>
                                                    <label for="client-name" class="block text-sm text-gray-900">
                                                        Buy Currency
                                                    </label>
                                                    <div class="mt-1 mb-3">
                                                        <v-select class="h-full mt-1" :options="currencies" label="name" :reduce="currencies => currencies.id" v-model="form.buy_currency_id" placeholder="Select Currency" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="client-name" class="block text-sm text-gray-900">
                                                        Sell Currency
                                                    </label>
                                                    <div class="mt-1 mb-3">
                                                        <v-select class="h-full mt-1" :options="currencies" label="name" :reduce="currencies => currencies.id" v-model="form.sell_currency_id" placeholder="Select Currency" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="quote-exchange-rate" class="block text-sm text-gray-900">
                                                        Exchange Rate
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="number" step="0.00001" id="quote-name" v-model="form.exchange_rate"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.exchange_rate}" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                        class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                        class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive, computed} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, ExclamationCircleIcon, ArrowCircleRightIcon, RefreshIcon} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors";
import {mapState, mapActions} from 'vuex'


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
        ArrowCircleRightIcon,
        RefreshIcon
    },
    computed: {
        ...mapState({
            // organisations: usePage().props.trading_details,
        }),
        new_quote_statuses() {
            const formattedStatuses = [];
    
            // Iterate over the keys and values of the statuses object
            for (const key in this.quote_statuses) {
                if (Object.hasOwnProperty.call(this.quote_statuses, key)) {
                    const id = key;
                    const status = this.quote_statuses[key];
                    
                    formattedStatuses.push({ id, status });
                }
            }
            
            return formattedStatuses;
        }
    },
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            clientOverride: ref(false),
            fixture: computed(() => usePage().props.fixture),
            quote_statuses: usePage().props.quote_statuses,
            updating: false,
            currencies: usePage().props.currencies,
            clients: usePage().props.clients,
            form: useForm({
                id: null,
                name: null,
                client_id: usePage().props.fixture.client.id,
                client: usePage().props.fixture.client.name,
                status: "Not Sent",
                fixture_id: usePage().props.fixture.id,
                decline_reason: null,
                exchange_rate: 1,
                buy_currency_id: 1,
                sell_currency_id: 1
            }),
        }
    },
    mounted() {
        this.getAllClients();
        
        // Preselect if fixture has a currency_id
        if (this.fixture?.primary_trading_details?.currency_id) {
            this.form.buy_currency_id = this.fixture.primary_trading_details.currency_id;
            this.form.sell_currency_id = this.fixture.primary_trading_details.currency_id;
        }
    },
    methods: {
        getAllClients() {
            this.$store.dispatch('organisations/getAllClients');
        },
        overrideClient() {
          this.clientOverride = true
        },
        revertToFixtureClient() {
            this.clientOverride = false
            this.form.client_id = this.fixture.client.id
        },
        submit() {
            if (this.updating) {
                this.form.post(route('frontend.fixture.quote.update', [this.fixture.id, this.form.id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();

                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            } else {
                this.form.post(route('frontend.fixture.quote.create', this.fixture.id), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            }
        },
        close() {
            this.form.reset();
            this.form.clearErrors();
            this.show = false;
        },

        open() {
            this.form.clearErrors();
            this.show = true;
        }
    }
}
</script>
